<div class="container container-full-page">
  <h1>
    Privacy Policy
  </h1>

  <h4>1 Application of Policy</h4>

  <p>Knight Frank is committed to protecting the privacy of personal information in accordance with its obligations
    under the <em>Privacy Act 1988</em> (Cth) (<strong>the Privacy Act</strong>) and, in particular, the Australian
    Privacy Principles (<strong>APPs</strong>).</p>

  <p>This policy applies to personal information collected by Knight Frank Australia Pty Ltd and its related bodies
    corporate in Australia (referred to in this policy as <strong>Knight Frank</strong>). In this policy, the
    <strong>Group </strong>means members of the Knight Frank Group globally.
  </p>

  <p>Under the Privacy Act, personal information is broadly defined. In simple terms it means any information about an
    individual (i.e. a natural person) who can be identified from the information, whether the information is true or
    not and whether recorded in a material form or not. </p>

  <h4>2 Handling of Personal Information</h4>

  <p>Knight Frank is committed to observing its obligations under the Privacy Act and APP’s. </p>

  <h5>2.1 What personal information do we collect?</h5>

  <p>Knight Frank collects personal information that is reasonably necessary to provide its services and to manage its
    relationships with clients and other parties.</p>

  <p>The types of personal information collected will depend on the services being provided and the circumstances of
    collection. For example, for property transactions, we may collect personal information from the parties involved
    (including vendors, purchasers, landlords, tenants etc.) such as name, contact information (i.e. e‑mail address,
    home and office address, telephone and fax numbers), date of birth, gender, marital status, tax file number,
    driver’s licence number and expiry date, details of employment and/or occupation, and financial information
    (including bank account information, information about your assets, occupation and income, account balances and
    payment history). We also collect credit information, including from credit checks, to assess credit worthiness.
  </p>

  <p>Knight Frank also collects personal information from members of the Group and other parties of the following kinds:
  </p>

  <p>· personal information of current, former and prospective corporate and consumer clients and suppliers, and
    individual contacts at current, former and prospective corporate and consumer clients and suppliers, including name,
    contact information, marketing communications subscribed to, wealth category, property requirements, property
    details (e.g. market value), strategy (e.g. opinions and decisions regarding exploitation of property assets),
    profile information, notes of previous meetings and viewings;</p>

  <p>· in connection with the administration of supplier service matters, including communication with suppliers,
    evaluation of, and payment for, services, administration of internal systems, and, compliance with company policies
    and legal requirements, personal information including contact information, notes of previous meetings and telephone
    calls. </p>

  <p>Knight Frank will sometimes need to collect personal information about individuals who are representatives of the
    organisations receiving Knight Frank’s services (such as business contact details). Knight Frank also collects
    personal information from job applicants seeking employment with Knight Frank.</p>

  <p>If it is necessary to collect sensitive information from an individual, their consent will be obtained.</p>

  <p>If you do not provide personal information when requested by Knight Frank, Knight Frank may be unable to provide
    you with its services.</p>

  <h5>2.2 How is personal information collected and held?</h5>

  <p>Knight Frank will collect personal information directly from the individual to whom it relates whenever
    practicable. This may be done verbally (e.g. in person or over the telephone), in writing (e.g. by letter, fax,
    email, application or other form) or through contact via our websites.</p>

  <p>In circumstances where it is not practicable to collect the information directly from the individual, Knight Frank
    may collect the information via third parties. This may occur, for example:</p>

  <ol class="alpha">
    <li>from the individual’s employer;</li>
    <li>from credit reference agencies or other credit reporting bodies and financial institutions or other credit
      providers;</li>
    <li>Knight Frank’s own records about you, from which Knight Frank may internally generate its own scores,
      assessments or deductions, particularly in relation to credit worthiness; </li>
    <li>from other agents and valuers who work with us;</li>
    <li>from property owners for whom we market properties or provide other services;</li>
    <li>from referees;</li>
    <li>from our contractors;</li>
    <li>from market research companies contracted by us to obtain information in relation to our services or from
      which we purchase mailing lists;</li>
    <li>from recruitment agencies; </li>
    <li>from publicly available information and databases; or</li>
    <li>from other members of the Group. </li>
  </ol>

  <p>From time to time, Knight Frank may include links on its website to the websites of other organisations. Knight
    Frank is not responsible for the content or privacy practices of those other websites or organisations.</p>

  <p>Personal information received by Knight Frank is held securely, either in electronic files on Knight Frank’s
    computer systems or in physical files held on Knight Frank’s premises.</p>

  <p>Cookies (small files placed on a user’s computer when you access a website) are used on our website. Most web
    browsers allow you to control whether to accept Cookies. Unless you elect not to accept Cookies on your browser, the
    Knight Frank website will automatically place a Cookie on your computer to enable you to make full use of our
    website’s functionality.</p>

  <h5>2.3 How do we use personal information?</h5>

  <p>Knight Frank generally holds and uses personal information for the following purposes:</p>

  <ol class="alpha">
    <li>to provide our services to clients;</li>
    <li>to manage third party relationships including with contractors and consultants;</li>
    <li>for security purposes (e.g. details collected at open inspections);</li>
    <li>to conduct marketing activities, including keeping individuals informed about our services and properties we
      are dealing with and direct marketing via social media;</li>
    <li>for the purposes of review and analysis of clients’ property requirements; communicating with clients
      regarding
      properties, opportunities and Knight Frank services they may find of value, communicating with clients to set up
      property viewings and meetings, providing ongoing advice to clients, including buying, selling, letting,
      management,
      development, investment and professional consultancy;</li>
    <li>for the purposes of the administration of supplier service matters, including communication with suppliers;
      evaluation of, and payment for, services; administration of internal systems, and compliance with company policies
      and legal requirements; </li>
    <li>to lawfully obtain credit information and to assess credit worthiness. </li>
  </ol>

  <h5>2.4 Disclosure of personal information to third parties</h5>

  <p>Knight Frank may disclose personal information to third parties – for example, to our contractors,
    customers/clients, franchisees, potential vendors, purchasers, landlords or tenants, media organisations, other
    agents and their customers/clients, owners’ corporations/bodies corporate, and to parties engaged to evaluate a
    property – for the purpose for which the information was collected or related purposes (such as to complete a
    transaction or provide a requested service).</p>

  <p>Personal information may also be disclosed to advisers (including but not limited to solicitors), financial
    institutions and other credit providers, credit reporting bodies, government and statutory bodies, and to third
    party contractors engaged by Knight Frank (e.g. valuers, debt collection services, printing and mailing services,
    payroll and accounting services, auditors, information technology, marketing service providers).</p>

  <p>Knight Frank may disclose personal information to other members of the Group (both within and outside Australia)
    where necessary for our purposes. </p>

  <p><strong>Overseas disclosure</strong></p>

  <p>Personal information may be disclosed to overseas recipients, including but not limited to members of the Group.
    Overseas recipients are likely to be located in the United Kingdom and Singapore.</p>

  <p>Where we disclose personal information to another party, we will require the third party to use the personal
    information only for the specific purposes for which we supply the information.</p>

  <h5>2.5 Direct marketing, including via social media</h5>

  <p>Knight Frank may use and disclose your personal information in order to inform you of products and services that
    may be of interest to you, including via social media platforms. In the event you do not wish to receive such
    communications, you can opt-out by contacting Knight Frank’s Privacy Officer via the contact details set out at
    section 2.7 and 2.9 below or through any opt-out mechanism contained in a marketing communication to you. </p>

  <h5>2.6 Safeguarding your personal information</h5>

  <p>Knight Frank has technological and operational processes and procedures in place to protect personal information
    from misuse, interference and loss, unauthorised access, modification and disclosure.</p>

  <p>Notwithstanding Knight Frank’s commitment to safeguarding your personal information, if an ‘eligible data breach’
    occurs (within the meaning of the Privacy Act), we will notify you and the Office of the Australian Information
    Commissioner (<strong>OAIC</strong>) that this has occurred. An ‘eligible data breach’ broadly refers to the
    situation where there has been an unauthorised access to, or unauthorised disclosure of, your personal information
    held by Knight Frank in circumstances where a reasonable person would conclude that the access or disclosure would
    likely result in serious harm to you.</p>

  <h5>2.7 How to request access or correct personal information</h5>

  <p>Under the Privacy Act, you have the right to seek access to and correction of personal information we hold about
    you, subject to some exceptions.</p>

  <p>To request access or to correct your personal information, or to obtain more information about Knight Frank’s
    privacy policy, you can contact Knight Frank’s Privacy Officer:</p>

  <p>
    By telephone: 03 9604 4700
    <br>
    By letter to: The Privacy Officer, Knight Frank, Level 29, 120 Collins Street, Melbourne 3000
    <br>
    By email: <a href="mailto:privacy@au.knightfrank.com">privacy&#64;au.knightfrank.com</a>
  </p>

  <p>Knight Frank will endeavour to respond to requests for access to or correction of personal information within a
    reasonable period of time.</p>

  <p>Where we refuse access, we will where possible provide you with a written notice setting out our reasons for the
    refusal and the avenues for complaint about the refusal.</p>

  <p>We may charge a fee for providing access to personal information. No fee is charged for the making of a request for
    access or correction.</p>

  <h5>2.8 How to opt out</h5>

  <p>If you do not wish to receive any further information from Knight Frank, including direct marketing materials as
    referred to above at section 2.5, you can contact the person named on the last communication you received from
    Knight Frank. Alternatively, if receiving emails from Knight Frank, you will have the option to unsubscribe from our
    mailing list by following the directions on our emails or you can opt-out by contacting Knight Frank directly via
    the contact details set out below.</p>

  <h5>2.9 How to get more information or make a complaint</h5>

  <p>Please direct any questions about this policy or complaints about a breach of privacy by Knight Frank to Knight
    Frank’s Privacy Officer who can be contacted:</p>

  <p>
    By telephone: 03 9604 4700
    <br>
    By letter to: The Privacy Officer, Knight Frank, Level 29, 120 Collins Street, Melbourne 3000
    <br>
    By email: <a href="mailto:privacy@au.knightfrank.com">privacy&#64;au.knightfrank.com</a>
  </p>

  <p>Any complaint should be made in writing. At all times, privacy complaints:</p>

  <ul>
    <li>will be treated seriously;</li>
    <li>will be dealt with promptly;</li>
    <li>will be dealt with in a confidential manner; and</li>
    <li>will not affect your existing obligations or affect the commercial arrangements between you and Knight Frank.
    </li>
  </ul>

  <p>Knight Frank’s Privacy Officer will look into your complaint. You will be informed of the outcome of your complaint
    following completion of those inquiries. In the event that you are dissatisfied with the outcome of your complaint,
    or an extension to the time in which Knight Frank will resolve it, you may refer the complaint to the OAIC. </p>
</div>

<br>
<br>